import React, { useState, useEffect } from 'react';
import './Wallet.css';
import { Box, Typography } from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import { useNavigate } from 'react-router-dom';
import KoinsIcon from '../../../assets/WalletIcon/koins-icon.svg';
import WalletCardIcon from '../../../assets/WalletIcon/wallet-card-icon.svg';
import TransactionHistory from '../../../components/Wallet/TransactionHistory/TransactionHistory';
import { useTranslation } from '../../../i18n/LanguageContext';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

const Wallet = () => {
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [walletDetails, setWalletDetails] = useState(null);
  const [conversionRate, setConversionRate] = useState(7.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Format currency values
  const formatCurrency = value => {
    if (!value) return '0';
    // Convert to number and format with commas
    const numValue = parseFloat(value);
    return numValue.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Format riyal values with 2 decimal places
  const formatRiyal = value => {
    if (!value) return '0.00';
    const numValue = parseFloat(value);
    return numValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    const fetchWalletDetails = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/teachers/wallet_details`);
        setWalletDetails(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching wallet details:', err);
        setError('Failed to load wallet details. Please try again later.');
        toast.error('Failed to load wallet details');
      } finally {
        setLoading(false);
      }
    };

    const fetchConversionRate = async () => {
      try {
        const response = await axiosInstance.get(`/auth/koins-to-riyal`);
        setConversionRate(response.data.conversion_rate);
      } catch (err) {
        console.error('Error fetching conversion rate:', err);
        // Fallback to default rate if API fails
        setConversionRate(7.5);
      }
    };

    fetchWalletDetails();
    fetchConversionRate();
  }, []);

  return (
    <Layout title="Klass Online | Wallet">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Box className="wallet-container p-[1rem]">
              <Box className="wallet-flex-container">
                <Box className="flex items-center gap-2">
                  <Typography
                    className="navigation-text"
                    onClick={() => navigate('/instructor/account')}
                  >
                    {translate('wallet', 'myAccountText') || 'My Account'} {'>'}
                    {'>'} {translate('wallet', 'walletText')} {'>'}
                    {'>'}
                  </Typography>
                  <Typography className="profile-text">
                    {translate('wallet', 'detailsText')}
                  </Typography>
                </Box>
                <Typography className="wallet-title">
                  {translate('wallet', 'transactionDetailsText')}
                </Typography>
              </Box>
              <Box className="flex items-center gap-3">
                <Box className="single-wallet-card">
                  <Box className="flex flex-col gap-2">
                    <div className="total-balance-text">
                      {translate('wallet', 'totalBalanceText')}
                    </div>
                    <div className="total-balance-value">
                      {formatCurrency(walletDetails?.total_earnings)} Koins
                    </div>
                    {/* <div className="exchange-rate-text">
                      {formatRiyal(walletDetails?.total_earnings * conversionRate)} Qatari Riyal
                    </div> */}
                  </Box>
                  <img src={KoinsIcon} alt="Koins" />
                </Box>
                <Box className="second-wallet-card">
                  <Box className="icon-container">
                    <img src={WalletCardIcon} alt="Wallet" />
                  </Box>
                  <Box className="flex flex-col gap-1">
                    <div className="vasted-text">{translate('wallet', 'vestedText')}</div>
                    <div className="vasted-value">
                      {formatCurrency(walletDetails?.locked_earnings)} Koins
                    </div>
                  </Box>
                </Box>
                <Box className="second-wallet-card">
                  <Box className="icon-container">
                    <img src={WalletCardIcon} alt="Wallet" />
                  </Box>
                  <Box className="flex flex-col gap-1">
                    <div className="vasted-text">
                      {translate('wallet', 'availableForWithdrawalText') ||
                        'Available for Withdrawal'}
                    </div>
                    <div className="vasted-value">
                      {formatCurrency(
                        parseFloat(walletDetails?.total_earnings || 0) -
                          parseFloat(walletDetails?.locked_earnings || 0)
                      )}{' '}
                      Koins
                    </div>
                  </Box>
                </Box>
                <Box className="second-wallet-card">
                  <Box className="icon-container">
                    <img src={WalletCardIcon} alt="Wallet" />
                  </Box>
                  <Box className="flex flex-col gap-1">
                    <div className="vasted-text">
                      {translate('wallet', 'lastWithdrawalText') || 'Last Withdrawal'}
                    </div>
                    <div className="vasted-value">
                      {formatCurrency(walletDetails?.last_withdrawal)} Koins
                    </div>
                  </Box>
                </Box>
              </Box>
              <TransactionHistory />
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Wallet;
